// Applicants.js — Hub screen: entry point for all applicant management
// Tabs: Pipeline | Pool | AI Selection | Analytics
// Navigates to: ApplicantsPipelineScreen, ApplicantsPoolScreen, ApplicantSelectionScreen, ApplicantDetailsScreen

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity,
  TextInput, FlatList, Image, Alert, Modal, Dimensions, RefreshControl,
  ActivityIndicator, Animated, StatusBar, Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import {
  RecruitmentAPI, STATUS_CFG, STAGES, normaliseApplicant,
  avatarColor, initials, fmtDate, timeAgo, loadCompanyData, getStatusColor,
} from './recruitmentShared';

const { width } = Dimensions.get('window');

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name, uri, size = 44 }) => {
  const [err, setErr] = React.useState(false);
  if (uri && !err) {
    return (
      <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden' }}>
        <Image source={{ uri }} style={{ width: size, height: size }} onError={() => setErr(true)} />
      </View>
    );
  }
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: avatarColor(name),
      alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#fff', fontSize: size * 0.32, fontWeight: '900' }}>{initials(name)}</Text>
    </View>
  );
};

const StagePill = ({ stage }) => {
  const cfg = STATUS_CFG[stage] || STATUS_CFG.New;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: cfg.bg,
      borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 }}>
      <MaterialCommunityIcons name={cfg.icon} size={12} color={cfg.color} />
      <Text style={{ fontSize: 11, color: cfg.color, fontWeight: '700', marginLeft: 4 }}>{stage || 'New'}</Text>
    </View>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function Applicants({ navigation, route }) {
  const [companyLogo, setCompanyLogo]  = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName]  = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId]      = useState(null);

  const [activeTab, setActiveTab]      = useState('overview');
  const [applicants, setApplicants]    = useState([]);
  const [jobs, setJobs]                = useState([]);
  const [stats, setStats]              = useState({ total: 0, new: 0, shortlisted: 0, interviewed: 0, offered: 0, hired: 0, rejected: 0 });
  const [loading, setLoading]          = useState(true);
  const [refreshing, setRefreshing]    = useState(false);
  const [search, setSearch]            = useState('');
  const [filterStage, setFilterStage]  = useState('all');

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── Load ──────────────────────────────────────────────────────────────────
  useFocusEffect(useCallback(() => {
    loadCompanyData(setCompanyLogo, setCompanyName, setCompanyId);
  }, []));

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoading(true);
    const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10) || null;
    if (!cid) {
      setLoading(false);
      Alert.alert('No company', 'Could not determine your company. Please sign in again.');
      return;
    }
    setCompanyId(cid);
    await Promise.all([fetchApplicants(cid), fetchJobs(cid), fetchStats(cid)]);
    setLoading(false);
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
  };

  const fetchApplicants = async (cid) => {
    try {
      const res = await RecruitmentAPI.getApplicants({ company_id: cid, limit: 200 });
      const raw = Array.isArray(res) ? res
                : Array.isArray(res?.data)         ? res.data
                : Array.isArray(res?.rows)         ? res.rows
                : Array.isArray(res?.applications) ? res.applications
                : [];
      setApplicants(raw.map(normaliseApplicant).filter(Boolean));
    } catch (e) { console.warn('fetchApplicants:', e?.message); }
  };

  const fetchJobs = async (cid) => {
    try {
      const res = await RecruitmentAPI.getJobs(cid);
      const rows = Array.isArray(res) ? res : (res?.data || res?.rows || []);
      setJobs(rows);
    } catch (e) { console.warn('fetchJobs:', e?.message); }
  };

  const fetchStats = async (cid) => {
    try {
      const res = await RecruitmentAPI.getDashboardStats(cid);
      // The new endpoint returns { by_stage, by_status, rolling, totals }.
      // Older callers expected { data: { ... } }. Surface whichever shape we get.
      if (res?.totals) {
        setStats({
          total: res.totals.total_applications,
          active: res.totals.active_applications,
          upcomingInterviews: res.totals.upcoming_interviews,
          pendingOffers: res.totals.pending_offers,
          activeJobs: res.totals.active_jobs,
          applied_7d: res.rolling?.applied_7d || 0,
          applied_30d: res.rolling?.applied_30d || 0,
          by_stage: res.by_stage || [],
          by_status: res.by_status || [],
          ...(res.data || {}),
        });
      } else if (res?.data) {
        setStats(res.data);
      }
    } catch (e) { console.warn('fetchStats:', e?.message); }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchApplicants(companyId), fetchStats(companyId)]);
    setRefreshing(false);
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const sharedParams = { companyId, companyLogo, companyName };

  const filteredApplicants = applicants.filter(a => {
    const matchSearch = !search ||
      String(a.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
      String(a.email ?? '').toLowerCase().includes(search.toLowerCase()) ||
      a.jobTitle?.toLowerCase().includes(search.toLowerCase());
    const matchStage = filterStage === 'all' || (a.stage || '').toLowerCase() === filterStage;
    return matchSearch && matchStage;
  });

  const stageCount = (s) => applicants.filter(a => (a.stage || '').toLowerCase() === s.toLowerCase()).length;

  // ── Navigation helpers ────────────────────────────────────────────────────
  const openApplicant = (applicant) => {
    navigation.navigate('ApplicantDetailsScreen', { ...sharedParams, applicant, applicantId: applicant.id });
  };

  const openPipeline = (jobId = null, jobTitle = '') => {
    navigation.navigate('ApplicantsPipelineScreen', { ...sharedParams, jobId, jobTitle, filterByJob: !!jobId });
  };

  const openPool = () => {
    navigation.navigate('ApplicantsPoolScreen', { ...sharedParams, filterByJob: false });
  };

  const openSelection = (jobId = null, jobTitle = '') => {
    navigation.navigate('ApplicantSelectionScreen', { ...sharedParams, jobId, jobTitle });
  };

  // ── Renders ───────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96', '#1e7be2']} style={S.header}>
      <View style={S.headerRow}>
        <TouchableOpacity style={S.hBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={20} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, alignItems: 'center' }}>
          {companyLogo
            ? <Image source={{ uri: companyLogo }} style={S.hLogo} />
            : <View style={S.hLogoFb}><Text style={{ color: '#fff', fontWeight: '800', fontSize: 13 }}>{(companyName || 'C')[0].toUpperCase()}</Text></View>}
        </View>
        <View style={{ flex: 3, marginLeft: 8 }}>
          <Text style={S.hTitle}>Applicants</Text>
          <Text style={S.hSub}>{companyName || 'All Applications'} · {applicants.length} total</Text>
        </View>
        <TouchableOpacity style={S.hBtn} onPress={() => openPipeline()}>
          <MaterialCommunityIcons name="pipe" size={20} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={[S.hBtn, { marginLeft: 8 }]} onPress={() => openSelection()}>
          <MaterialCommunityIcons name="robot-outline" size={20} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Quick stats */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 14, paddingTop: 8, paddingBottom: 12, gap: 10 }}>
        {[
          { label: 'Total', value: applicants.length, color: '#fff' },
          { label: 'New', value: stageCount('new'), color: '#93C5FD' },
          { label: 'Shortlisted', value: stageCount('shortlisted'), color: '#6EE7B7' },
          { label: 'Interviewed', value: stageCount('interviewed'), color: '#C4B5FD' },
          { label: 'Offered', value: stageCount('offered'), color: '#FCD34D' },
          { label: 'Hired', value: stageCount('hired'), color: '#34D399' },
          { label: 'Rejected', value: stageCount('rejected'), color: '#FCA5A5' },
        ].map(s => (
          <TouchableOpacity key={s.label} style={S.statChip}
            onPress={() => { setActiveTab('list'); setFilterStage(s.label === 'Total' ? 'all' : String(s.label ?? '').toLowerCase()); }}>
            <Text style={[S.statVal, { color: s.color }]}>{s.value}</Text>
            <Text style={S.statLbl}>{s.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={S.tabsRow}>
      {[
        { id: 'overview',   label: 'Overview',  icon: 'view-dashboard-outline' },
        { id: 'list',       label: 'All',       icon: 'format-list-bulleted' },
        { id: 'by_job',     label: 'By Job',    icon: 'briefcase-outline' },
        { id: 'analytics',  label: 'Analytics', icon: 'chart-bar' },
      ].map(t => (
        <TouchableOpacity key={t.id} style={[S.tab, activeTab === t.id && S.tabActive]}
          onPress={() => setActiveTab(t.id)}>
          <MaterialCommunityIcons name={t.icon} size={16} color={activeTab === t.id ? '#0e4e96' : '#9CA3AF'} />
          <Text style={[S.tabText, activeTab === t.id && S.tabTextActive]}>{t.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderOverview = () => (
    <ScrollView contentContainerStyle={{ padding: 16 }}>
      {/* Quick actions */}
      <Text style={S.sectionTitle}>Quick Actions</Text>
      <View style={S.actionGrid}>
        {[
          { label: 'All Pipeline', icon: 'pipe', color: '#0e4e96', bg: '#EFF6FF', onPress: () => openPipeline() },
          { label: 'Applicant Pool', icon: 'account-group', color: '#7c3aed', bg: '#F5F3FF', onPress: openPool },
          { label: 'AI Selection', icon: 'robot-outline', color: '#059669', bg: '#ECFDF5', onPress: () => openSelection() },
          { label: 'Compare', icon: 'compare', color: '#d97706', bg: '#FFFBEB', onPress: () => Alert.alert('Select 2+ applicants in list view to compare') },
        ].map((a, i) => (
          <TouchableOpacity key={i} style={[S.actionCard, { backgroundColor: a.bg }]} onPress={a.onPress}>
            <MaterialCommunityIcons name={a.icon} size={28} color={a.color} />
            <Text style={[S.actionLabel, { color: a.color }]}>{a.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Stage funnel */}
      <Text style={[S.sectionTitle, { marginTop: 20 }]}>Recruitment Funnel</Text>
      <View style={S.funnelCard}>
        {STAGES.filter(s => s.key !== 'technical').map((s, i) => {
          const count = stageCount(s.key);
          const pct = applicants.length ? Math.round((count / applicants.length) * 100) : 0;
          return (
            <TouchableOpacity key={s.key} style={S.funnelRow} onPress={() => { setActiveTab('list'); setFilterStage(s.key); }}>
              <View style={[S.funnelDot, { backgroundColor: s.color }]} />
              <Text style={S.funnelLabel}>{s.label}</Text>
              <View style={S.funnelBarBg}>
                <View style={[S.funnelBarFill, { width: `${pct}%`, backgroundColor: s.color }]} />
              </View>
              <Text style={[S.funnelCount, { color: s.color }]}>{count}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Recent applicants */}
      <View style={S.sectionRow}>
        <Text style={S.sectionTitle}>Recent Applications</Text>
        <TouchableOpacity onPress={() => setActiveTab('list')}>
          <Text style={S.sectionLink}>View all →</Text>
        </TouchableOpacity>
      </View>
      {applicants.slice(0, 5).map(a => renderApplicantCard(a))}
    </ScrollView>
  );

  const renderApplicantCard = (item) => (
    <TouchableOpacity key={item.id} style={S.card} onPress={() => openApplicant(item)} activeOpacity={0.85}>
      <View style={S.cardRow}>
        <Avatar name={item.name} uri={item.avatar} size={44} />
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={S.cardName} numberOfLines={1}>{item.name}</Text>
          <Text style={S.cardRole} numberOfLines={1}>{item.currentRole || item.jobTitle || '—'}</Text>
          <Text style={S.cardCompany} numberOfLines={1}>{item.currentCompany || item.location || ''}</Text>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 4 }}>
          <StagePill stage={item.stage?.charAt(0).toUpperCase() + item.stage?.slice(1) || 'New'} />
          {item.aiScore && (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <MaterialCommunityIcons name="robot-outline" size={12} color="#8B5CF6" />
              <Text style={{ fontSize: 11, color: '#8B5CF6', fontWeight: '700', marginLeft: 3 }}>{item.aiScore}%</Text>
            </View>
          )}
        </View>
      </View>
      <View style={S.cardFooter}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <Ionicons name="time-outline" size={12} color="#9CA3AF" />
          <Text style={S.cardMeta}>{item.appliedAt ? timeAgo(item.appliedAt) : '—'}</Text>
        </View>
        {item.jobTitle && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <MaterialCommunityIcons name="briefcase-outline" size={12} color="#9CA3AF" />
            <Text style={S.cardMeta} numberOfLines={1}>{item.jobTitle}</Text>
          </View>
        )}
        <TouchableOpacity style={S.viewBtn} onPress={() => openApplicant(item)}>
          <Text style={S.viewBtnText}>View</Text>
          <Ionicons name="chevron-forward" size={12} color="#0e4e96" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderList = () => (
    <View style={{ flex: 1 }}>
      {/* Search + filter */}
      <View style={S.searchBar}>
        <Ionicons name="search" size={18} color="#9CA3AF" />
        <TextInput style={S.searchInput} value={search} onChangeText={setSearch}
          placeholder="Search applicants..." placeholderTextColor="#9CA3AF" />
        {search.length > 0 && <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={18} color="#9CA3AF" /></TouchableOpacity>}
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 14, paddingBottom: 8, gap: 8 }}>
        {[{ key: 'all', label: 'All' }, ...STAGES].map(s => (
          <TouchableOpacity key={s.key} style={[S.fChip, filterStage === s.key && S.fChipActive]}
            onPress={() => setFilterStage(s.key)}>
            <Text style={[S.fChipText, filterStage === s.key && { color: '#fff' }]}>{s.label || s.key}</Text>
            <Text style={[S.fChipText, filterStage === s.key && { color: 'rgba(255,255,255,0.8)' }]}>
              {' '}({s.key === 'all' ? applicants.length : stageCount(s.key)})
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <FlatList
        data={filteredApplicants}
        keyExtractor={i => i.id}
        renderItem={({ item }) => renderApplicantCard(item)}
        contentContainerStyle={{ padding: 16 }}
        ListEmptyComponent={
          <View style={S.empty}>
            <MaterialCommunityIcons name="account-search-outline" size={56} color="#D1D5DB" />
            <Text style={S.emptyText}>{search ? 'No results found' : 'No applicants yet'}</Text>
          </View>
        }
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}
      />
    </View>
  );

  const renderByJob = () => (
    <ScrollView contentContainerStyle={{ padding: 16 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}>
      {jobs.length === 0 ? (
        <View style={S.empty}>
          <MaterialCommunityIcons name="briefcase-outline" size={56} color="#D1D5DB" />
          <Text style={S.emptyText}>No active jobs found</Text>
        </View>
      ) : jobs.map(job => {
        const jobApps = applicants.filter(a => a.jobId === String(job.id));
        return (
          <TouchableOpacity key={job.id} style={S.jobCard}
            onPress={() => openPipeline(job.id, job.title || job.job_title)}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={S.jobIconBox}>
                <MaterialCommunityIcons name="briefcase" size={22} color="#0e4e96" />
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={S.jobTitle} numberOfLines={1}>{job.title || job.job_title || 'Role'}</Text>
                <Text style={S.jobMeta}>{job.department || job.location || ''}</Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={S.jobCount}>{jobApps.length}</Text>
                <Text style={{ fontSize: 10, color: '#9CA3AF' }}>applicants</Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color="#9CA3AF" style={{ marginLeft: 8 }} />
            </View>
            {/* Mini stage breakdown */}
            {jobApps.length > 0 && (
              <View style={{ flexDirection: 'row', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {STAGES.slice(0, 5).map(s => {
                  const cnt = jobApps.filter(a => a.stage === s.key).length;
                  if (!cnt) return null;
                  return (
                    <View key={s.key} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 }}>
                      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: s.color, marginRight: 4 }} />
                      <Text style={{ fontSize: 11, color: '#374151', fontWeight: '600' }}>{s.label}: {cnt}</Text>
                    </View>
                  );
                })}
              </View>
            )}
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  const renderAnalytics = () => {
    const total = applicants.length || 1;
    return (
      <ScrollView contentContainerStyle={{ padding: 16 }}>
        {/* KPI grid */}
        <View style={S.kpiGrid}>
          {[
            { label: 'Total Applications', value: applicants.length, icon: 'account-group', color: '#0e4e96' },
            { label: 'Active Pipeline', value: applicants.filter(a => !['hired','rejected'].includes(a.stage)).length, icon: 'pipe', color: '#8B5CF6' },
            { label: 'Hire Rate', value: `${Math.round((stageCount('hired') / total) * 100)}%`, icon: 'check-decagram', color: '#10B981' },
            { label: 'Reject Rate', value: `${Math.round((stageCount('rejected') / total) * 100)}%`, icon: 'close-circle', color: '#EF4444' },
          ].map((k, i) => (
            <View key={i} style={[S.kpiCard, { borderTopColor: k.color }]}>
              <MaterialCommunityIcons name={k.icon} size={24} color={k.color} />
              <Text style={[S.kpiVal, { color: k.color }]}>{k.value}</Text>
              <Text style={S.kpiLbl}>{k.label}</Text>
            </View>
          ))}
        </View>

        {/* Stage conversion */}
        <Text style={[S.sectionTitle, { marginTop: 8 }]}>Stage Conversion</Text>
        <View style={S.funnelCard}>
          {STAGES.map(s => {
            const cnt = stageCount(s.key);
            const pct = Math.round((cnt / total) * 100);
            return (
              <View key={s.key} style={S.funnelRow}>
                <View style={[S.funnelDot, { backgroundColor: s.color }]} />
                <Text style={[S.funnelLabel, { width: 90 }]}>{s.label}</Text>
                <View style={S.funnelBarBg}>
                  <View style={[S.funnelBarFill, { width: `${pct}%`, backgroundColor: s.color }]} />
                </View>
                <Text style={[S.funnelCount, { color: s.color }]}>{cnt}</Text>
                <Text style={[S.funnelCount, { color: '#9CA3AF', marginLeft: 4, width: 36 }]}>{pct}%</Text>
              </View>
            );
          })}
        </View>

        {/* AI Score distribution */}
        {applicants.filter(a => a.aiScore).length > 0 && (
          <>
            <Text style={[S.sectionTitle, { marginTop: 16 }]}>AI Score Distribution</Text>
            <View style={S.funnelCard}>
              {[
                { label: 'Excellent (90+)', min: 90, color: '#10B981' },
                { label: 'Good (75–89)', min: 75, max: 90, color: '#3B82F6' },
                { label: 'Average (60–74)', min: 60, max: 75, color: '#F59E0B' },
                { label: 'Below 60', max: 60, color: '#EF4444' },
              ].map((r, i) => {
                const cnt = applicants.filter(a => a.aiScore && (r.min === undefined || a.aiScore >= r.min) && (r.max === undefined || a.aiScore < r.max)).length;
                const pct = Math.round((cnt / total) * 100);
                return (
                  <View key={i} style={S.funnelRow}>
                    <View style={[S.funnelDot, { backgroundColor: r.color }]} />
                    <Text style={[S.funnelLabel, { width: 120 }]}>{r.label}</Text>
                    <View style={S.funnelBarBg}>
                      <View style={[S.funnelBarFill, { width: `${pct}%`, backgroundColor: r.color }]} />
                    </View>
                    <Text style={[S.funnelCount, { color: r.color }]}>{cnt}</Text>
                  </View>
                );
              })}
            </View>
          </>
        )}
      </ScrollView>
    );
  };

  const renderContent = () => {
    if (loading) return <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size={40} color="#0e4e96" /><Text style={{ marginTop: 12, color: '#9CA3AF' }}>Loading applicants...</Text></View>;
    switch (activeTab) {
      case 'overview':   return renderOverview();
      case 'list':       return renderList();
      case 'by_job':     return renderByJob();
      case 'analytics':  return renderAnalytics();
      default:           return renderOverview();
    }
  };

  return (
    <SafeAreaView style={S.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}
      {renderTabs()}
      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        {renderContent()}
      </Animated.View>
    </SafeAreaView>
  );
}

const S = StyleSheet.create({
  container:    { flex: 1, backgroundColor: '#F3F4F6' },
  header:       { paddingBottom: 4 },
  headerRow:    { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingTop: Platform.OS === 'android' ? 28 : 8, paddingBottom: 6 },
  hBtn:         { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  hLogo:        { width: 34, height: 34, borderRadius: 17, borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' },
  hLogoFb:      { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  hTitle:       { fontSize: 17, fontWeight: '900', color: '#fff' },
  hSub:         { fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 },
  statChip:     { backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 8, alignItems: 'center', minWidth: 56 },
  statVal:      { fontSize: 18, fontWeight: '900' },
  statLbl:      { fontSize: 10, color: 'rgba(255,255,255,0.75)', marginTop: 2 },

  tabsRow:      { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingHorizontal: 8 },
  tab:          { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 11 },
  tabActive:    { borderBottomWidth: 2, borderBottomColor: '#0e4e96' },
  tabText:      { fontSize: 11, color: '#9CA3AF', fontWeight: '600' },
  tabTextActive:{ color: '#0e4e96', fontWeight: '800' },

  sectionTitle: { fontSize: 15, fontWeight: '800', color: '#111827', marginBottom: 12 },
  sectionRow:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionLink:  { fontSize: 13, color: '#0e4e96', fontWeight: '600' },

  actionGrid:   { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 8 },
  actionCard:   { width: (width - 44) / 2, borderRadius: 14, padding: 16, alignItems: 'center', gap: 8 },
  actionLabel:  { fontSize: 13, fontWeight: '700', textAlign: 'center' },

  funnelCard:   { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  funnelRow:    { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  funnelDot:    { width: 10, height: 10, borderRadius: 5, marginRight: 8 },
  funnelLabel:  { fontSize: 12, color: '#374151', fontWeight: '500', width: 80 },
  funnelBarBg:  { flex: 1, height: 8, backgroundColor: '#F3F4F6', borderRadius: 4, marginHorizontal: 8, overflow: 'hidden' },
  funnelBarFill:{ height: 8, borderRadius: 4 },
  funnelCount:  { fontSize: 12, fontWeight: '700', width: 28, textAlign: 'right' },

  card:         { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  cardRow:      { flexDirection: 'row', alignItems: 'flex-start' },
  cardName:     { fontSize: 15, fontWeight: '800', color: '#111827' },
  cardRole:     { fontSize: 12, color: '#6B7280', marginTop: 2 },
  cardCompany:  { fontSize: 11, color: '#9CA3AF', marginTop: 1 },
  cardFooter:   { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F8FAFC' },
  cardMeta:     { fontSize: 11, color: '#9CA3AF' },
  viewBtn:      { marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: 2 },
  viewBtnText:  { fontSize: 12, color: '#0e4e96', fontWeight: '700' },

  searchBar:    { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingHorizontal: 14, paddingVertical: 10, gap: 8 },
  searchInput:  { flex: 1, fontSize: 14, color: '#111827' },
  fChip:        { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#F3F4F6', borderWidth: 1, borderColor: '#E5E7EB' },
  fChipActive:  { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  fChipText:    { fontSize: 12, color: '#6B7280', fontWeight: '600' },

  jobCard:      { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  jobIconBox:   { width: 44, height: 44, borderRadius: 10, backgroundColor: '#EFF6FF', alignItems: 'center', justifyContent: 'center' },
  jobTitle:     { fontSize: 15, fontWeight: '700', color: '#111827' },
  jobMeta:      { fontSize: 12, color: '#6B7280', marginTop: 2 },
  jobCount:     { fontSize: 20, fontWeight: '900', color: '#0e4e96' },

  kpiGrid:      { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  kpiCard:      { width: (width - 44) / 2, backgroundColor: '#fff', borderRadius: 12, padding: 14, alignItems: 'center', borderTopWidth: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  kpiVal:       { fontSize: 22, fontWeight: '900', marginTop: 6 },
  kpiLbl:       { fontSize: 11, color: '#9CA3AF', marginTop: 3, textAlign: 'center' },

  empty:        { alignItems: 'center', paddingVertical: 50 },
  emptyText:    { fontSize: 15, color: '#9CA3AF', marginTop: 12 },
});
